import {
  Alert,
  Card,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { parseCookies } from 'nookies';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { withDataPoint } from '../../../diagnostics/client';
import { KameleeonGoal } from '../../../experimentation/base';
import { trackConversion } from '../../../experimentation/client';
import {
  PatientGuardianInput,
  PatientInput,
  PatientPrimaryPolicyholderInput,
} from '../../../generated/graphql';
import { MinorPolicyHolderRelationLabelValueMap } from '../../../helpers/assessment';
import { dayToDate } from '../../../helpers/day';
import {
  formatAddressLine1,
  formatAddressLine2,
  formatBirthDate,
  formatFullName,
  formatPhoneNumber,
} from '../../../helpers/format-helpers';
import { useAppDispatch } from '../../../pages/assessment';
import {
  selectMedicalInfoAnswers,
  selectPatient,
  selectPatientGuardians,
  selectReasonVisitAnswers,
  setMedicalInfoAnswers,
  submitAssessment,
} from '../../../redux/assessment';
import { selectExperiments } from '../../../redux/experiments';
import { hasOtherPlan } from '../../../redux/helpers/assessment';
import { selectLdFlags } from '../../../redux/ld-flags';
import { setReview, updateMeta } from '../../../redux/meta';
import metaScreenConfig from '../../../redux/meta-config/screens';
import { Screens } from '../../../redux/types';
import { theme } from '../../../styles/theme';
import { Icon } from '../../icon/icon';
import { AssessmentButtons } from '../shared';

interface ICodeToQuestion {
  description: string;
  isEditable: boolean;
}

const codeToQuestion: Record<string, ICodeToQuestion> = {
  firstName: {
    description: 'First name',
    isEditable: true,
  },
  lastName: {
    description: 'Last name',
    isEditable: true,
  },
  birthDate: {
    description: 'Date of birth',
    isEditable: true,
  },
  phone: { description: 'Phone', isEditable: true },
  addressStreet1: { description: 'Street', isEditable: true },
  addressStreet2: { description: 'Unit', isEditable: true },
  addressZipCode: { description: 'Zip Code', isEditable: true },
  addressCity: { description: 'City', isEditable: true },
  addressState: { description: 'State', isEditable: true },
  email: { description: 'Email', isEditable: true },
  primaryGuardian: { description: 'Primary guardian', isEditable: true },
  secondaryGuardian: { description: 'Secondary guardian', isEditable: true },
  RV_MainReason: {
    description: 'Reason(s) for seeking psychiatric care?',
    isEditable: false,
  },
  RV_UseOfDrugs: { description: 'Drug use', isEditable: true },
  RV_DrinkingAlcohol: { description: 'Alcohol use', isEditable: true },
  RV_SecondaryReason: {
    description: 'Additional concerns?',
    isEditable: false,
  },
  RV_SpecialtyCare: { description: 'Specialized needs?', isEditable: false },
  MI_InsuranceProvider: {
    description: 'Insurance Provider?',
    isEditable: true,
  },
  MI_InsurancePlan: { description: 'Insurance Plan?', isEditable: true },
  MI_PrimaryPolicyHolderDetails: {
    description: 'Policyholder',
    isEditable: true,
  },
  MI_PrescriptionMedication: {
    description: 'Currently taking medication?',
    isEditable: true,
  },
  MI_MedicationList: { description: 'Current Medication', isEditable: true },
  MI_PrimaryPharmacy: {
    description: 'Do you have a primary pharmacy?',
    isEditable: true,
  },
  MI_PrimaryPharmacyDetails: {
    description: "What's your pharmacy information?",
    isEditable: true,
  },
  [Screens.MI_EmergencyContact]: {
    description: "Who's your emergency contact?",
    isEditable: true,
  },
  MI_HaveAppointment: {
    description: 'Do you already have an initial appointment scheduled?',
    isEditable: true,
  },
};

const getStringAssessmentValue = (key: string, it: string) => {
  if (key === 'MI_InsurancePlan' && hasOtherPlan(it))
    return "I don't see my plan";

  return it;
};

const DisplaySection = ({ title, dataObj }) => {
  const dispatch = useAppDispatch();
  // Note: We could have used the useExperimentation hook here (recommended, too),
  // but that would involve handling the loading of the feature flag value
  // (which is invalid by default). Much simpler to use what's in redux
  const { show_insurance_combo_screen } = useSelector(selectExperiments);
  const FREE_TEXT: string[] = dataObj.RV_MainReasonFreeText;

  if (FREE_TEXT?.length) {
    const RV_MainReason = [...dataObj.RV_MainReason];
    RV_MainReason.splice(dataObj.RV_MainReason.indexOf("I'm not sure"), 1);
    dataObj.RV_MainReason = RV_MainReason;
    dataObj.RV_MainReason.push(`I'm not sure: ${FREE_TEXT[0]}`);
  }

  return (
    <Card sx={{ mb: 2 }}>
      <Typography component="h3" variant="h5" sx={{ m: 3, mb: 0 }}>
        {title}
      </Typography>
      <List sx={{ pl: 3 }}>
        {Object.keys(codeToQuestion).map(key => {
          const question = codeToQuestion[key];
          const answerValue = question && dataObj[key];
          if (key === 'firstName') question.description = 'Patient';
          let value = null;

          if (typeof answerValue === 'string') {
            value = answerValue;
          } else if (typeof answerValue === 'object' && answerValue) {
            const mappableAnswer = Array.isArray(answerValue)
              ? answerValue
              : Object.values(answerValue);
            value = mappableAnswer
              .filter(answerVal => answerVal)
              .map((it, valueIndex: number) => {
                const displayValue =
                  typeof it === 'string'
                    ? getStringAssessmentValue(key, it)
                    : it.value;

                return (
                  <div key={`assessment-values-${key}-${valueIndex}`}>
                    {displayValue}
                  </div>
                );
              });
          }

          const handleEditClick = () => {
            const moveToScreen = (s: Screens) =>
              dispatch(updateMeta({ screen: s }));

            if (metaScreenConfig[key]) {
              moveToScreen(key as Screens);
            } else if (key.includes('Insurance')) {
              show_insurance_combo_screen
                ? moveToScreen(Screens.MI_InsuranceCombo)
                : moveToScreen(Screens.MI_InsuranceForm);
            } else if (key.includes('PrimaryPolicyHolder')) {
              if (show_insurance_combo_screen) {
                moveToScreen(Screens.MI_InsuranceCombo);
              } else {
                moveToScreen(Screens.MI_PrimaryPolicyHolder);
              }
            } else if (key.includes('Guardian')) {
              moveToScreen(Screens.GuardianDemographics);
            } else if (key.includes('Medication') || key.includes('Pharmacy')) {
              moveToScreen(Screens.MI_MedicationPharmacyCombo);
            } else {
              moveToScreen(Screens.CollectDemographics);
            }
          };

          return (
            value?.length && (
              <ListItem
                divider
                secondaryAction={
                  question.isEditable && (
                    <IconButton aria-label="Edit" onClick={handleEditClick}>
                      <Icon icon="Pen" fontSize="small" />
                    </IconButton>
                  )
                }
                sx={{ pl: 0, '&:last-child': { borderBottom: 0 } }}
              >
                <ListItemText
                  primary={question.description}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: theme.palette.text.secondary,
                  }}
                  secondary={value}
                  secondaryTypographyProps={{
                    variant: 'body1',
                    color: theme.palette.text.primary,
                  }}
                />
              </ListItem>
            )
          );
        })}
      </List>
    </Card>
  );
};

type DemographicReviewDetails = {
  firstName: string;
  lastName: string;
  birthDate: string;
  sex?: string;
  phone?: string;
  email?: string;
  addressLine1: string;
  addressLine2: string;
  relationToPatient?: string;
};

/**
 * Helper function for formatting demographic data to be shown in the
 * review DisplaySection components.
 *
 * @param demographicData the patient or guardians demographic data
 * @returns the formatted details for a demographic review `DisplaySection`
 */
const formatDemographicDetailsForReview = (
  demographicData?:
    | PatientInput
    | PatientGuardianInput
    | PatientPrimaryPolicyholderInput,
  addressOverride?: PatientInput
): DemographicReviewDetails => {
  if (!demographicData) return null;
  const addressData = addressOverride || demographicData;
  const { firstName, lastName, birthDate } = demographicData;

  const formatRelationToPatient = demographicDataObject => {
    const relationToPatient =
      'customRelation' in demographicDataObject &&
      demographicDataObject.relationToPatient === 'Other'
        ? demographicDataObject.customRelation
        : demographicDataObject.relationToPatient;

    return `Relation: ${relationToPatient}`;
  };

  return {
    firstName,
    lastName,
    birthDate: formatBirthDate(dayToDate(birthDate)),
    phone:
      'phone' in demographicData
        ? formatPhoneNumber(demographicData.phone)
        : undefined, // handles null
    email: 'email' in demographicData ? demographicData.email : undefined,
    sex: 'sex' in demographicData ? demographicData.sex : undefined,
    addressLine1: formatAddressLine1(addressData),
    addressLine2: formatAddressLine2(addressData),
    relationToPatient:
      'relationToPatient' in demographicData
        ? formatRelationToPatient(demographicData)
        : undefined,
  };
};

const Review = () => {
  const dispatch = useAppDispatch();
  const patient = useSelector(selectPatient);
  const patientGuardians = useSelector(selectPatientGuardians);
  const reasonVisitAnswers = useSelector(selectReasonVisitAnswers);
  const medicalInfoAnswers = useSelector(selectMedicalInfoAnswers);
  const { addToast } = useToasts();

  const [isContinuing, setIsContinuing] = useState(false);
  const flags = useSelector(selectLdFlags);

  let personalDetailsObject: any = { ...patient };

  const patientDetails = formatDemographicDetailsForReview(patient);
  const primaryGuardianDetails = formatDemographicDetailsForReview(
    patientGuardians?.primary,
    patientGuardians?.primary.isAddressLinkedWithPatient && patient
  );
  const secondaryGuardianDetails = formatDemographicDetailsForReview(
    patientGuardians?.secondary,
    patientGuardians?.secondary?.isAddressLinkedWithPatient && patient
  );

  personalDetailsObject = {
    firstName: patientDetails,
    primaryGuardian: primaryGuardianDetails,
    secondaryGuardian: secondaryGuardianDetails,
  };

  let medicalInfoObject: any = { ...medicalInfoAnswers };

  let formattedPolicyHolderDetails;

  const patientIsMinor = patientGuardians?.primary;

  if (!medicalInfoAnswers.MI_PrimaryPolicyHolderDetails) {
    formattedPolicyHolderDetails = formatFullName(patient);
  } else if (
    patientIsMinor &&
    medicalInfoAnswers.MI_PrimaryPolicyHolderDetails.relationToPatient !==
      MinorPolicyHolderRelationLabelValueMap.Other
  ) {
    formattedPolicyHolderDetails = formatFullName({
      firstName: medicalInfoAnswers.MI_PrimaryPolicyHolderDetails.firstName,
      lastName: medicalInfoAnswers.MI_PrimaryPolicyHolderDetails.lastName,
    });
  } else {
    formattedPolicyHolderDetails = formatDemographicDetailsForReview(
      medicalInfoAnswers.MI_PrimaryPolicyHolderDetails,
      medicalInfoAnswers.MI_PrimaryPolicyHolderDetails
        .isAddressLinkedWithPatient && patient
    );
  }

  medicalInfoObject = {
    ...medicalInfoAnswers,
    MI_PrimaryPolicyHolderDetails: formattedPolicyHolderDetails,
  };

  const policyholderDetailsObject = { ...medicalInfoAnswers }
    ?.MI_PrimaryPolicyHolderDetails;

  useEffect(() => {
    if (policyholderDetailsObject?.isAddressLinkedWithPatient && patient) {
      const updatedPolicyholderDetailsObject = {
        ...policyholderDetailsObject,
        addressStreet1: patient.addressStreet1,
        addressCity: patient.addressCity,
        addressState: patient.addressState,
        addressZipCode: patient.addressZipCode,
        ...(patient.addressStreet2 && {
          addressStreet2: patient.addressStreet2,
        }),
      };

      const payload = {
        MI_PrimaryPolicyHolderDetails: updatedPolicyholderDetailsObject,
      };
      dispatch(setMedicalInfoAnswers(payload));
    }
  }, []);

  useEffect(() => {
    dispatch(setReview());
  }, []);

  const cookies = parseCookies();
  const freshpaintCookieKey = Object.keys(cookies).find(
    key => key.endsWith('perfalytics') && cookies[key]
  );

  let googleClickId;
  if (freshpaintCookieKey) {
    const freshpaintCookie = cookies[freshpaintCookieKey];

    const freshpaintCookieValue = JSON.parse(freshpaintCookie);
    googleClickId = freshpaintCookieValue?.['$gclid'];
  }

  const onClick = async () => {
    setIsContinuing(true);

    await withDataPoint('intake_submit', async dp => {
      dp.running_ab_tests = {
        flags: Object.keys(flags).map(key => ({
          flag: key,
          value: flags[key],
        })),
      };
      const errors = await dispatch(submitAssessment(googleClickId));
      if (errors) {
        dp.error = true;
        addToast(
          'Sorry, something went wrong. Please refresh the page and try again.',
          { appearance: 'error' }
        );
      } else {
        dp.user_success = true;
      }
    });
    void trackConversion(KameleeonGoal.ReviewScreenCompleted);

    setIsContinuing(false);
  };

  return (
    <>
      <DisplaySection
        title="Personal Details"
        dataObj={personalDetailsObject}
      />
      <DisplaySection
        title="Reason for Visit"
        dataObj={{ ...reasonVisitAnswers }}
      />
      <DisplaySection title="Medical Information" dataObj={medicalInfoObject} />
      <Alert severity="info">
        Your responses will be used to find matching providers and cannot be
        changed after submitting.
      </Alert>
      <AssessmentButtons
        primary={{
          onClick,
          children: 'Submit',
          disabled: isContinuing,
          loading: isContinuing,
        }}
      />
    </>
  );
};

export default Review;
